import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import {
	Box,
	Typography,
	Button,
	TextField,
	Dialog,
	DialogContent,
	IconButton,
	CircularProgress,
	Switch,
	InputAdornment,
	Popover,
	Checkbox,
	useMediaQuery
} from '@mui/material'
import {
	Close as CloseIcon,
	Search as SearchIcon,
	Edit as EditIcon,
	Delete as DeleteIcon,
	Add as AddIcon,
	UploadFile as UploadFileIcon,
	PictureAsPdf as PdfIcon,
	ChevronLeft as ChevronLeftIcon,
	ChevronRight as ChevronRightIcon
} from '@mui/icons-material'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { Icon as Iconify } from '@iconify/react'
import {
	RichTextEditor,
	MenuControlsContainer,
	MenuSelectHeading,
	MenuDivider,
	MenuButtonBold,
	MenuButtonItalic,
	MenuButtonUnderline,
	MenuButtonStrikethrough,
	MenuButtonBulletedList,
	MenuButtonOrderedList,
	MenuButtonEditLink,
	LinkBubbleMenu,
	LinkBubbleMenuHandler
} from 'mui-tiptap'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/style.css'
import { supabase } from '../utils/supabase'
import dayjs from 'dayjs'
import theme from '../theme'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

const extensions = [
	StarterKit,
	Underline,
	Link.configure({ openOnClick: false }),
	LinkBubbleMenuHandler,
]

function frequencyFromToggles(autoSend, everyAppt) {
	if (everyAppt) return 'every_booking'
	if (autoSend) return 'once'
	return 'manual'
}

function togglesToFrequency(frequency) {
	if (frequency === 'every_booking') return { autoSend: true, everyAppt: true }
	if (frequency === 'once') return { autoSend: true, everyAppt: false }
	return { autoSend: false, everyAppt: false }
}

// ─── Filter helpers (adapted from Dashboard) ──────────────────────────────────

const QUICK_DATE_OPTIONS = [
	{ value: 'today', label: 'Today' },
	{ value: 'this_week', label: 'This Week' },
	{ value: 'last_week', label: 'Last Week' },
	{ value: 'this_month', label: 'This Month' },
	{ value: 'last_month', label: 'Last Month' },
	{ value: 'this_year', label: 'This Year' },
	{ value: 'last_year', label: 'Last Year' },
	{ value: 'all_time', label: 'All Time' },
]

const AGREEMENT_STATUS_OPTIONS = [
	{ value: 'all', label: 'All' },
	{ value: 'pending', label: 'Pending' },
	{ value: 'signed', label: 'Signed' },
]

function getQuickDateRange(key) {
	const now = new Date()
	const y = now.getFullYear()
	const m = now.getMonth()
	const d = now.getDate()
	const pad = (n) => String(n).padStart(2, '0')
	const fmt = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`
	const today = fmt(now)
	const dayOfWeek = now.getDay()
	const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1
	switch (key) {
		case 'today': return { from: today, to: today }
		case 'this_week': { const mon = new Date(y, m, d - mondayOffset); return { from: fmt(mon), to: today } }
		case 'last_week': { const pMon = new Date(y, m, d - mondayOffset - 7); const pSun = new Date(y, m, d - mondayOffset - 1); return { from: fmt(pMon), to: fmt(pSun) } }
		case 'this_month': return { from: `${y}-${pad(m + 1)}-01`, to: today }
		case 'last_month': { const fp = new Date(y, m - 1, 1); const lp = new Date(y, m, 0); return { from: fmt(fp), to: fmt(lp) } }
		case 'this_year': return { from: `${y}-01-01`, to: today }
		case 'last_year': return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` }
		case 'all_time': return null
		default: return { from: today, to: today }
	}
}

function AgrFilterButton({ label, value, onClick }) {
	return (
		<Box onClick={onClick} sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '8px 16px', bgcolor: '#FFFFFF', border: '1px solid #CBD5E0', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap', '&:hover': { bgcolor: '#F7FAFC' } }}>
			<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', color: 'rgba(0,0,0,0.65)' }}>{label}</Typography>
			<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '14px', color: '#000' }}>{value}</Typography>
		</Box>
	)
}

function AgrFilterDropdown({ anchorEl, open, onClose, options, selected, onSelect }) {
	return (
		<Popover open={open} anchorEl={anchorEl} onClose={onClose}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			slotProps={{ paper: { sx: { borderRadius: '12px', boxShadow: '0px 16px 32px -8px rgba(12,12,13,0.4)', p: '12px', mt: '4px' } } }}>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '140px' }}>
				{options.map(opt => (
					<Box key={opt.value} onClick={() => { onSelect(opt.value); onClose() }}
						sx={{ p: '10px 8px', borderRadius: '7px', cursor: 'pointer', bgcolor: selected === opt.value ? 'rgba(0,0,0,0.1)' : 'transparent', '&:hover': { bgcolor: selected === opt.value ? 'rgba(0,0,0,0.12)' : 'rgba(0,0,0,0.04)' } }}>
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '14px', color: '#000' }}>{opt.label}</Typography>
					</Box>
				))}
			</Box>
		</Popover>
	)
}

function AgrDateFilterDropdown({ anchorEl, open, onClose, isMobile, selectedQuickDate, onSelectQuickDate, customRange, onApplyCustomRange }) {
	const [fromDate, setFromDate] = useState(customRange?.from || '')
	const [toDate, setToDate] = useState(customRange?.to || '')
	const [pickerRange, setPickerRange] = useState({ from: undefined, to: undefined })
	const [mobilePickerOpen, setMobilePickerOpen] = useState(false)

	const parseYmd = (v) => { if (!v) return undefined; const [yy, mm, dd] = v.split('-').map(Number); if (!yy || !mm || !dd) return undefined; return new Date(yy, mm - 1, dd) }
	const formatYmd = (date) => { if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''; const pad = (n) => String(n).padStart(2, '0'); return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` }

	useEffect(() => {
		if (!open) return
		setFromDate(customRange?.from || '')
		setToDate(customRange?.to || '')
		setPickerRange({ from: parseYmd(customRange?.from), to: parseYmd(customRange?.to) })
	}, [open, customRange?.from, customRange?.to])

	const canApply = Boolean(fromDate && toDate && fromDate <= toDate)

	const dayPickerProps = {
		mode: 'range',
		resetOnSelect: true,
		numberOfMonths: 1,
		pagedNavigation: true,
		showOutsideDays: true,
		weekStartsOn: 1,
		selected: pickerRange,
		onSelect: (range) => {
			const next = range || { from: undefined, to: undefined }
			if (pickerRange.from && !pickerRange.to && next.from && next.to && formatYmd(next.from) !== formatYmd(pickerRange.from)) {
				const reset = { from: next.from, to: undefined }
				setPickerRange(reset); setFromDate(formatYmd(reset.from)); setToDate('')
				return
			}
			setPickerRange(next); setFromDate(formatYmd(next.from)); setToDate(formatYmd(next.to))
		}
	}

	return (
		<>
			<Popover open={open} anchorEl={anchorEl} onClose={onClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				slotProps={{ paper: { sx: { borderRadius: '12px', boxShadow: '0px 16px 32px -8px rgba(12,12,13,0.4)', p: '12px', mt: '4px' } } }}>
				<Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px' }}>
					{/* Quick picks */}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '170px' }}>
						{QUICK_DATE_OPTIONS.map(opt => (
							<Box key={opt.value} onClick={() => { onSelectQuickDate(opt.value); onClose() }}
								sx={{ p: '10px 8px', borderRadius: '7px', cursor: 'pointer', bgcolor: selectedQuickDate === opt.value ? 'rgba(0,0,0,0.1)' : 'transparent', '&:hover': { bgcolor: selectedQuickDate === opt.value ? 'rgba(0,0,0,0.12)' : 'rgba(0,0,0,0.04)' } }}>
								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '14px', color: '#000' }}>{opt.label}</Typography>
							</Box>
						))}
						{isMobile && (
							<Box onClick={() => { setMobilePickerOpen(true); onClose() }}
								sx={{ p: '10px 8px', borderRadius: '7px', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}>
								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '14px', color: '#5B4FF2' }}>Custom range…</Typography>
							</Box>
						)}
					</Box>

					{!isMobile && <Box sx={{ width: '1px', bgcolor: 'rgba(0,0,0,0.08)', alignSelf: 'stretch' }} />}

					{/* Desktop calendar */}
					{!isMobile && (
						<Box sx={{ minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '13px', color: '#4A5568' }}>Custom range</Typography>
							<Box sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', p: '8px', '& .rdp': { '--rdp-accent-color': '#9F7AEA', '--rdp-background-color': '#F3E8FF', margin: 0, fontFamily: 'Inter, sans-serif', fontSize: '13px' }, '& .rdp-months': { gap: '10px' }, '& .rdp-month': { margin: 0 }, '& .rdp-day_button': { borderRadius: '8px', width: '34px', height: '34px' } }}>
								<DayPicker {...dayPickerProps} />
							</Box>
							<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
								<Button variant="contained" size="small" disabled={!canApply}
									onClick={() => { if (!canApply) return; onApplyCustomRange({ from: fromDate, to: toDate }); onClose() }}
									sx={{ textTransform: 'none', bgcolor: '#9F7AEA', '&:hover': { bgcolor: '#805AD5' } }}>
									Apply
								</Button>
							</Box>
						</Box>
					)}
				</Box>
			</Popover>

			{/* Mobile bottom sheet */}
			<Dialog open={mobilePickerOpen} onClose={() => setMobilePickerOpen(false)} fullWidth
				PaperProps={{ sx: { position: 'fixed', bottom: 0, left: 0, right: 0, m: 0, borderRadius: '16px 16px 0 0', maxHeight: '85vh', width: '100%', maxWidth: '100%' } }}>
				<DialogContent sx={{ p: '16px 20px 32px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '16px', color: '#1A202C' }}>Custom range</Typography>
						<IconButton size="small" onClick={() => setMobilePickerOpen(false)}><CloseIcon fontSize="small" /></IconButton>
					</Box>
					<Box sx={{ display: 'flex', gap: '8px' }}>
						{['From', 'To'].map((label, idx) => {
							const val = idx === 0 ? fromDate : toDate
							return (
								<Box key={label} sx={{ flex: 1, bgcolor: '#F7FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', p: '8px 12px' }}>
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#718096', fontWeight: 500 }}>{label}</Typography>
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: val ? '#1A202C' : '#A0AEC0', fontWeight: 500, mt: '2px' }}>{val || '—'}</Typography>
								</Box>
							)
						})}
					</Box>
					<Box sx={{ '& .rdp': { '--rdp-accent-color': '#9F7AEA', '--rdp-background-color': '#F3E8FF', margin: '0 auto', fontFamily: 'Inter, sans-serif', fontSize: '13px', width: '100%' }, '& .rdp-months': { justifyContent: 'center' }, '& .rdp-month': { width: '100%' }, '& .rdp-table': { width: '100%' }, '& .rdp-day_button': { borderRadius: '8px', width: '38px', height: '38px' } }}>
						<DayPicker {...dayPickerProps} />
					</Box>
					<Button variant="contained" fullWidth disabled={!canApply}
						onClick={() => { if (!canApply) return; onApplyCustomRange({ from: fromDate, to: toDate }); setMobilePickerOpen(false) }}
						sx={{ textTransform: 'none', borderRadius: '10px', py: '12px', bgcolor: '#9F7AEA', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '15px', '&:hover': { bgcolor: '#805AD5' }, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#A0AEC0' } }}>
						Apply
					</Button>
				</DialogContent>
			</Dialog>
		</>
	)
}

function PaginationControls({ page, totalCount, pageSize, onPageChange }) {
	const totalPages = Math.ceil(totalCount / pageSize)
	if (totalPages <= 1) return null
	return (
		<Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', pt: '12px' }}>
			<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#718096' }}>
				{page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalCount)} of {totalCount}
			</Typography>
			<IconButton size="small" disabled={page === 0} onClick={() => onPageChange(page - 1)} sx={{ '&:hover': { bgcolor: '#F3E8FF' } }}>
				<ChevronLeftIcon sx={{ fontSize: 20, color: page === 0 ? '#CBD5E0' : '#2D3748' }} />
			</IconButton>
			<IconButton size="small" disabled={page >= totalPages - 1} onClick={() => onPageChange(page + 1)} sx={{ '&:hover': { bgcolor: '#F3E8FF' } }}>
				<ChevronRightIcon sx={{ fontSize: 20, color: page >= totalPages - 1 ? '#CBD5E0' : '#2D3748' }} />
			</IconButton>
		</Box>
	)
}

// ─────────────────────────────────────────────────────────────────────────────

export default function Agreements({ merchant }) {
	const [activeTab, setActiveTab] = useState('sent')
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	const PAGE_SIZE = 10

	// Sent agreements state
	const [sentAgreements, setSentAgreements] = useState([])
	const [sentTotalCount, setSentTotalCount] = useState(0)
	const [sentPage, setSentPage] = useState(0)
	const [sentLoading, setSentLoading] = useState(false)
	const [sentSearch, setSentSearch] = useState('')
	const [sentStatusFilter, setSentStatusFilter] = useState('all')
	const [sentDateKey, setSentDateKey] = useState('this_month')
	const [sentCustomRange, setSentCustomRange] = useState(null)
	const [sentSortKey, setSentSortKey] = useState('date')
	const [sentSortDir, setSentSortDir] = useState('desc')

	// Filter bar anchor state
	const [statusAnchor, setStatusAnchor] = useState(null)
	const [dateAnchor, setDateAnchor] = useState(null)

	// Manage agreements state
	const [agreements, setAgreements] = useState([])
	const [manageLoading, setManageLoading] = useState(false)
	const [manageSearch, setManageSearch] = useState('')

	// Summary counts
	const [totalSent, setTotalSent] = useState(0)
	const [pendingCount, setPendingCount] = useState(0)
	const [signedCount, setSignedCount] = useState(0)
	const [expiringSoonCount, setExpiringSoonCount] = useState(0)

	// Open the signed agreement viewer (AgreementSignPage) in a new tab.
	// signed_agreements.id is the canonical URL param the page already
	// supports for both signing and viewing a completed signature.
	const handleOpenSigView = (row) => {
		if (row.status !== 'signed' || !row.id) return
		window.open(`/agreements/${row.id}`, '_blank', 'noopener,noreferrer')
	}

	// Dialog state
	const [dialogOpen, setDialogOpen] = useState(false)
	const [dialogTab, setDialogTab] = useState('content') // for edit mode only
	const [editingAgreement, setEditingAgreement] = useState(null)
	const [agreementTitle, setAgreementTitle] = useState('')
	const [isEditMode, setIsEditMode] = useState(false)
	const editSnapshotRef = useRef(null)
	const [agreementContent, setAgreementContent] = useState('')
	const [autoSend, setAutoSend] = useState(false)
	const [everyAppt, setEveryAppt] = useState(false)
	const [saving, setSaving] = useState(false)

	// Re-sign dialog state (shown after modifying every_booking agreements with existing signatures)
	const [resignDialogOpen, setResignDialogOpen] = useState(false)
	const [resignAffectedCount, setResignAffectedCount] = useState(0)
	const [resignNewAgreementId, setResignNewAgreementId] = useState(null)

	// PDF state
	const [contentMode, setContentMode] = useState('rich_text') // 'rich_text' | 'pdf'
	const [pdfFile, setPdfFile] = useState(null)           // newly selected File
	const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null) // object URL
	const [existingPdfPath, setExistingPdfPath] = useState(null) // path already in DB
	const [pdfNumPages, setPdfNumPages] = useState(null)
	const [isDragOver, setIsDragOver] = useState(false)
	const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false)
	const fileInputRef = useRef(null)

	const editorRef = useRef(null)

	// Signatures tab state (edit dialog)
	const [sigTabData, setSigTabData] = useState([])
	const [sigTabTotalCount, setSigTabTotalCount] = useState(0)
	const [sigTabPage, setSigTabPage] = useState(0)
	const [sigTabLoading, setSigTabLoading] = useState(false)
	const [sigTabActionAnchor, setSigTabActionAnchor] = useState(null)
	const [sigTabActionRow, setSigTabActionRow] = useState(null)

	// Send agreement dialog state
	const [sendDialogOpen, setSendDialogOpen] = useState(false)
	const [sendAllClients, setSendAllClients] = useState([])
	const [sendClientStatuses, setSendClientStatuses] = useState({})
	const [sendClientsLoading, setSendClientsLoading] = useState(false)
	const [sendClientSearch, setSendClientSearch] = useState('')
	const [sendSelectedIds, setSendSelectedIds] = useState(new Set())
	const [sendFilterTab, setSendFilterTab] = useState('all')
	const [sendSending, setSendSending] = useState(false)
	const [sendProgress, setSendProgress] = useState(null)

	const fetchSentAgreements = useCallback(async (page = 0) => {
		if (!merchant?.id) return
		setSentLoading(true)
		try {
			const from = page * PAGE_SIZE
			const to = from + PAGE_SIZE - 1

			const dateRange = sentDateKey && sentDateKey !== 'custom' ? getQuickDateRange(sentDateKey) : sentCustomRange
			let pageQuery = supabase
				.from('signed_agreements')
				.select(`
					id, status, created_at, signed_at, expiry_date, signature_path,
					agreements!inner(id, title),
					clients!inner(id, name, last_name),
					appointments(id, start_ts)
				`, { count: 'exact' })
				.eq('merchant_id', merchant.id)
			if (sentStatusFilter !== 'all') {
				pageQuery = pageQuery.eq('status', sentStatusFilter)
			}
			if (dateRange?.from && dateRange?.to) {
				const startTs = new Date(`${dateRange.from}T00:00:00`).toISOString()
				const endTs = new Date(`${dateRange.to}T23:59:59.999`).toISOString()
				pageQuery = pageQuery.gte('created_at', startTs).lte('created_at', endTs)
			}

			const ascending = sentSortDir === 'asc'
			switch (sentSortKey) {
				case 'agreement':
					pageQuery = pageQuery.order('title', { foreignTable: 'agreements', ascending })
					break
				case 'client':
					pageQuery = pageQuery
						.order('name', { foreignTable: 'clients', ascending })
						.order('last_name', { foreignTable: 'clients', ascending })
					break
				case 'status':
					pageQuery = pageQuery.order('status', { ascending })
					break
				case 'date':
				default:
					pageQuery = pageQuery.order('created_at', { ascending })
					break
			}
			pageQuery = pageQuery.range(from, to)

			const [pageRes, allRes] = await Promise.all([
				pageQuery,
				supabase
					.from('signed_agreements')
					.select('id, status, appointments(start_ts)')
					.eq('merchant_id', merchant.id),
			])

			if (pageRes.error) throw pageRes.error
			setSentAgreements(pageRes.data || [])
			setSentTotalCount(pageRes.count || 0)

			const allData = allRes.data || []
			const now = new Date()
			const twoDaysFromNow = new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000)
			setTotalSent(allData.length)
			setSignedCount(allData.filter(s => s.status === 'signed').length)
			setPendingCount(allData.filter(s => s.status === 'pending').length)
			setExpiringSoonCount(allData.filter(s => {
				if (s.status !== 'pending') return false
				const startTs = s.appointments?.start_ts ? new Date(s.appointments.start_ts) : null
				if (!startTs) return false
				return startTs > now && startTs <= twoDaysFromNow
			}).length)
		} catch (err) {
			console.error('Failed to fetch sent agreements:', err)
		} finally {
			setSentLoading(false)
		}
	}, [merchant?.id, sentStatusFilter, sentDateKey, sentCustomRange, sentSortKey, sentSortDir])

	const fetchAgreements = useCallback(async () => {
		if (!merchant?.id) return
		setManageLoading(true)
		try {
			const { data, error } = await supabase
				.from('agreements')
				.select('*')
				.eq('merchant_id', merchant.id)
				.is('deleted_at', null)
				.order('created_at', { ascending: false })

			if (error) throw error
			setAgreements(data || [])
		} catch (err) {
			console.error('Failed to fetch agreements:', err)
		} finally {
			setManageLoading(false)
		}
	}, [merchant?.id])

	const [sigTabAllStatuses, setSigTabAllStatuses] = useState([])

	const fetchSigTabData = useCallback(async (agreementId, page = 0) => {
		if (!merchant?.id || !agreementId) return
		setSigTabLoading(true)
		try {
			const from = page * PAGE_SIZE
			const to = from + PAGE_SIZE - 1
			const [pageRes, statusRes] = await Promise.all([
				supabase
					.from('signed_agreements')
					.select(`
						id, status, created_at, signed_at, expiry_date, signature_path,
						agreements!inner(id, title),
						clients!inner(id, name, last_name),
						appointments(id, start_ts)
					`, { count: 'exact' })
					.eq('merchant_id', merchant.id)
					.eq('agreement_id', agreementId)
					.order('created_at', { ascending: false })
					.range(from, to),
				supabase
					.from('signed_agreements')
					.select('id, status, appointments(start_ts)')
					.eq('merchant_id', merchant.id)
					.eq('agreement_id', agreementId),
			])
			if (pageRes.error) throw pageRes.error
			setSigTabData(pageRes.data || [])
			setSigTabTotalCount(pageRes.count || 0)
			setSigTabAllStatuses(statusRes.data || [])
		} catch (err) {
			console.error('Failed to fetch signature tab data:', err)
		} finally {
			setSigTabLoading(false)
		}
	}, [merchant?.id])

	useEffect(() => {
		if (activeTab === 'sent') fetchSentAgreements(sentPage)
		else fetchAgreements()
	}, [activeTab, sentPage, fetchSentAgreements, fetchAgreements])

	useEffect(() => {
		setSentPage(0)
	}, [sentStatusFilter, sentDateKey, sentCustomRange, sentSortKey, sentSortDir])

	useEffect(() => {
		if (dialogTab === 'signatures' && editingAgreement?.id) {
			fetchSigTabData(editingAgreement.id, sigTabPage)
		}
	}, [dialogTab, editingAgreement?.id, sigTabPage, fetchSigTabData])

	const resetPdfState = () => {
		setPdfFile(null)
		if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl)
		setPdfPreviewUrl(null)
		setExistingPdfPath(null)
		setPdfNumPages(null)
		setIsDragOver(false)
	}

	const openNewAgreement = () => {
		setEditingAgreement(null)
		setAgreementTitle('')
		setAgreementContent('')
		setAutoSend(false)
		setEveryAppt(false)
		setContentMode('rich_text')
		resetPdfState()
		setDialogOpen(true)
	}

	const openEditAgreement = async (agreement) => {
		setEditingAgreement(agreement)
		setAgreementTitle(agreement.title)
		setIsEditMode(false)
		const hasPdf = !!agreement.content_path
		setContentMode(hasPdf ? 'pdf' : 'rich_text')
		setAgreementContent(hasPdf ? '' : (agreement.content || ''))
		setExistingPdfPath(hasPdf ? agreement.content_path : null)
		setPdfFile(null)
		setPdfNumPages(null)
		setIsDragOver(false)
		const toggles = togglesToFrequency(agreement.frequency)
		setAutoSend(toggles.autoSend)
		setEveryAppt(toggles.everyAppt)
		setDialogTab('signatures')
		setSigTabPage(0)

		// Load a signed URL so the existing PDF can be previewed
		if (hasPdf) {
			const { data } = await supabase.storage
				.from('agreements')
				.createSignedUrl(agreement.content_path, 60 * 60)
			setPdfPreviewUrl(data?.signedUrl || null)
		} else {
			setPdfPreviewUrl(null)
		}

		setDialogOpen(true)
	}

	const handlePdfFileSelect = (file) => {
		if (!file || file.type !== 'application/pdf') return
		setPdfFile(file)
		if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl)
		setPdfPreviewUrl(URL.createObjectURL(file))
		setPdfNumPages(null)
	}

	const handleSaveAgreement = async () => {
		if (!agreementTitle.trim()) return
		if (contentMode === 'pdf' && !pdfFile && !existingPdfPath) {
			alert('Please upload a PDF file.')
			return
		}
		setSaving(true)
		try {
			const frequency = frequencyFromToggles(autoSend, everyAppt)
			const now = new Date().toISOString()
			let newAgreementId = null

			if (contentMode === 'pdf') {
				let contentPath = existingPdfPath

				if (pdfFile) {
					const { data: placeholder, error: placeholderErr } = await supabase
						.from('agreements')
						.insert({
							merchant_id: merchant.id,
							title: agreementTitle.trim(),
							content: null,
							content_path: 'pending',
							frequency,
							enabled: true,
							...(editingAgreement ? { required_for_existing_clients: false } : {}),
						})
						.select('id')
						.single()
					if (placeholderErr) throw placeholderErr
					newAgreementId = placeholder.id

					const storagePath = `${merchant.id}/${placeholder.id}.pdf`
					const { error: uploadErr } = await supabase.storage
						.from('agreements')
						.upload(storagePath, pdfFile, { upsert: true, contentType: 'application/pdf' })
					if (uploadErr) throw uploadErr

					const { error: updateErr } = await supabase
						.from('agreements')
						.update({ content_path: storagePath, updated_at: now })
						.eq('id', placeholder.id)
					if (updateErr) throw updateErr
					contentPath = storagePath

					if (editingAgreement) {
						await supabase
							.from('agreements')
							.update({ enabled: false, deleted_at: now })
							.eq('id', editingAgreement.id)
					}
				} else if (editingAgreement) {
					await supabase
						.from('agreements')
						.update({ enabled: false, deleted_at: now })
						.eq('id', editingAgreement.id)

					const { data: inserted, error: insertErr } = await supabase
						.from('agreements')
						.insert({
							merchant_id: merchant.id,
							title: agreementTitle.trim(),
							content: null,
							content_path: contentPath,
							frequency,
							enabled: true,
							required_for_existing_clients: false,
						})
						.select('id')
						.single()
					if (insertErr) throw insertErr
					newAgreementId = inserted.id
				}

			} else {
				const newRecord = {
					merchant_id: merchant.id,
					title: agreementTitle.trim(),
					content: agreementContent || '<p></p>',
					content_path: null,
					frequency,
					enabled: true,
				}

				if (editingAgreement) {
					await supabase
						.from('agreements')
						.update({ enabled: false, deleted_at: now })
						.eq('id', editingAgreement.id)

					const { data: inserted, error } = await supabase
						.from('agreements')
						.insert({ ...newRecord, required_for_existing_clients: false })
						.select('id')
						.single()
					if (error) throw error
					newAgreementId = inserted.id
				} else {
					const { error } = await supabase.from('agreements').insert(newRecord)
					if (error) throw error
				}
			}

			// If editing a once agreement, show re-sign confirmation dialog.
			// We default the new agreement to "Keep existing signatures" (the
			// inserts above set required_for_existing_clients=false, and we
			// migrate signed_one_time_agreement_ids here) so that if the
			// merchant refreshes before answering the dialog, the safe path
			// has already been applied.
			if (editingAgreement && frequency === 'once' && newAgreementId) {
				const { count } = await supabase
					.from('signed_agreements')
					.select('client_id', { count: 'exact', head: true })
					.eq('agreement_id', editingAgreement.id)
					.eq('status', 'signed')

				const { data: signersRows } = await supabase
					.from('clients')
					.select('id, signed_one_time_agreement_ids')
					.eq('merchant_id', merchant.id)
					.contains('signed_one_time_agreement_ids', [editingAgreement.id])
				for (const c of signersRows || []) {
					const ids = c.signed_one_time_agreement_ids || []
					if (!ids.includes(newAgreementId)) {
						await supabase
							.from('clients')
							.update({
								signed_one_time_agreement_ids: [...new Set([...ids, newAgreementId])],
							})
							.eq('id', c.id)
					}
				}

				setResignAffectedCount(count || 0)
				setResignNewAgreementId(newAgreementId)
				setDialogOpen(false)
				setResignDialogOpen(true)
				fetchAgreements()
				if (activeTab === 'sent') fetchSentAgreements(sentPage)
				return
			}

			setDialogOpen(false)
			fetchAgreements()
			if (activeTab === 'sent') fetchSentAgreements(sentPage)
		} catch (err) {
			console.error('Failed to save agreement:', err)
			alert('Failed to save agreement: ' + err.message)
		} finally {
			setSaving(false)
		}
	}

	const handleResignChoice = async (requireResign) => {
		if (resignNewAgreementId && editingAgreement) {
			try {
				// Modified agreements default to "Keep existing signatures"
				// (required_for_existing_clients=false + migrated
				// signed_one_time_agreement_ids) so a refresh before answering
				// the dialog is safe. Only the "Require re-signatures" path
				// needs to undo that and force re-signing.
				if (requireResign) {
					await supabase
						.from('agreements')
						.update({ required_for_existing_clients: true })
						.eq('id', resignNewAgreementId)

					if (editingAgreement.frequency === 'once') {
						const { data: signersRows } = await supabase
							.from('clients')
							.select('id, signed_one_time_agreement_ids')
							.eq('merchant_id', merchant.id)
							.contains('signed_one_time_agreement_ids', [resignNewAgreementId])

						for (const c of signersRows || []) {
							const ids = c.signed_one_time_agreement_ids || []
							const next = ids.filter(id => id !== resignNewAgreementId)
							if (next.length !== ids.length) {
								await supabase
									.from('clients')
									.update({ signed_one_time_agreement_ids: next })
									.eq('id', c.id)
							}
						}
					}
				}
			} catch (err) {
				console.error('Failed to apply re-sign choice:', err)
			}
		}
		setResignDialogOpen(false)
		setResignNewAgreementId(null)
		setResignAffectedCount(0)
		fetchAgreements()
	}

	// Note: there is no longer a cancel/undo path after editing an agreement
	// — the "Agreement Updated" dialog forces the merchant to choose either
	// "Require re-signatures" or "Keep existing signatures". If the dialog
	// is dismissed (e.g., page reload), the new agreement is already in the
	// "Keep existing signatures" state by default (see save flow above).

	// Whether the merchant has modified the settings tab (auto-send / every-
	// appointment toggles) compared to the editing agreement's saved state.
	const settingsChanged = useMemo(() => {
		if (!editingAgreement) return false
		const currentFreq = frequencyFromToggles(autoSend, everyAppt)
		return currentFreq !== editingAgreement.frequency
	}, [editingAgreement, autoSend, everyAppt])

	// Whether the merchant has actually changed anything on the Content tab
	// since entering edit mode. Compared against the snapshot taken when
	// "Edit" was clicked. Used to gate the "Update Agreement" button so it
	// is hidden when there is nothing to commit.
	const contentChanged = useMemo(() => {
		if (!isEditMode) return false
		const snap = editSnapshotRef.current
		if (!snap) return false
		if ((snap.title || '') !== (agreementTitle || '')) return true
		if (snap.contentMode !== contentMode) return true
		if (contentMode === 'rich_text') {
			return (snap.content || '') !== (agreementContent || '')
		}
		if (snap.pdfFile !== pdfFile) return true
		if ((snap.existingPdfPath || '') !== (existingPdfPath || '')) return true
		return false
	}, [isEditMode, agreementTitle, agreementContent, contentMode, pdfFile, existingPdfPath])

	// Revert in-dialog changes for the active tab without closing the dialog.
	// - Content tab (edit mode): restore content/title/PDF state from the
	//   snapshot taken when entering edit mode, then exit edit mode.
	// - Settings tab: revert toggles to the editing agreement's saved
	//   frequency.
	const handleCancelEdit = () => {
		if (dialogTab === 'content') {
			const snap = editSnapshotRef.current
			if (snap) {
				if ('title' in snap) setAgreementTitle(snap.title)
				setAgreementContent(snap.content)
				setContentMode(snap.contentMode)
				setPdfFile(snap.pdfFile)
				setExistingPdfPath(snap.existingPdfPath)
			}
			setIsEditMode(false)
		} else if (dialogTab === 'settings' && editingAgreement) {
			const toggles = togglesToFrequency(editingAgreement.frequency)
			setAutoSend(toggles.autoSend)
			setEveryAppt(toggles.everyAppt)
		}
	}

	const handleDeleteAgreement = async (agreementId) => {
		if (!window.confirm('Delete this agreement? Existing signed records will be preserved.')) return
		try {
			const { error } = await supabase
				.from('agreements')
				.update({ enabled: false, deleted_at: new Date().toISOString() })
				.eq('id', agreementId)
			if (error) throw error
			fetchAgreements()
			fetchSentAgreements(sentPage)
		} catch (err) {
			console.error('Failed to delete agreement:', err)
			alert('Failed to delete: ' + err.message)
		}
	}

	const handleToggleEnabled = async (agreement) => {
		// Never re-enable a soft-deleted record
		if (agreement.deleted_at) return
		try {
			const { error } = await supabase
				.from('agreements')
				.update({ enabled: !agreement.enabled })
				.eq('id', agreement.id)
			if (error) throw error
			fetchAgreements()
		} catch (err) {
			console.error('Failed to toggle agreement:', err)
		}
	}

	// Sent agreements table action popover state
	const [sentActionAnchor, setSentActionAnchor] = useState(null)
	const [sentActionRow, setSentActionRow] = useState(null)

	const handleMarkSignedInPerson = async (row, source) => {
		try {
			const { error } = await supabase
				.from('signed_agreements')
				.update({ status: 'signed', signed_at: new Date().toISOString() })
				.eq('id', row.id)
			if (error) throw error
			if (source === 'sent') {
				setSentActionAnchor(null)
				setSentActionRow(null)
			} else {
				setSigTabActionAnchor(null)
				setSigTabActionRow(null)
			}
			if (editingAgreement?.id) fetchSigTabData(editingAgreement.id, sigTabPage)
			fetchSentAgreements(sentPage)
		} catch (err) {
			console.error('Failed to mark as signed in-person:', err)
		}
	}

	const fetchSendDialogClients = useCallback(async (agreementId) => {
		if (!merchant?.id || !agreementId) return
		setSendClientsLoading(true)
		try {
			const [clientsRes, sigRes] = await Promise.all([
				supabase.from('clients').select('id, name, last_name, email, phone_number').eq('merchant_id', merchant.id).order('name'),
				supabase.from('signed_agreements').select('client_id, status').eq('agreement_id', agreementId).eq('merchant_id', merchant.id),
			])
			setSendAllClients(clientsRes.data || [])
			const statusMap = {}
			for (const sa of (sigRes.data || [])) {
				const prev = statusMap[sa.client_id]
				if (!prev || sa.status === 'signed') statusMap[sa.client_id] = sa.status
			}
			setSendClientStatuses(statusMap)
		} catch (err) {
			console.error('Failed to fetch send dialog clients:', err)
		} finally {
			setSendClientsLoading(false)
		}
	}, [merchant?.id])

	useEffect(() => {
		if (sendDialogOpen && editingAgreement?.id) {
			fetchSendDialogClients(editingAgreement.id)
		}
	}, [sendDialogOpen, editingAgreement?.id, fetchSendDialogClients])

	const handleSendToClients = async () => {
		if (sendSelectedIds.size === 0 || !editingAgreement?.id) return
		setSendSending(true)
		const clientIds = [...sendSelectedIds]
		const total = clientIds.length
		setSendProgress({ sent: 0, total })

		try {
			const BATCH_SIZE = 20
			for (let i = 0; i < clientIds.length; i += BATCH_SIZE) {
				const batch = clientIds.slice(i, i + BATCH_SIZE)
				const res = await supabase.functions.invoke('send-agreement', {
					body: { agreementId: editingAgreement.id, clientIds: batch, merchantId: merchant.id, channel: 'email' },
				})
				if (res.error) console.error('Batch send error:', res.error)
				setSendProgress({ sent: Math.min(i + BATCH_SIZE, total), total })
			}

			setSendProgress({ sent: total, total })
			await new Promise(r => setTimeout(r, 600))
			setSendDialogOpen(false)
			setSendSelectedIds(new Set())
			setSendProgress(null)
			fetchSigTabData(editingAgreement.id, sigTabPage)
			fetchSentAgreements(sentPage)
		} catch (err) {
			console.error('Failed to send agreements:', err)
			alert('Failed to send: ' + err.message)
		} finally {
			setSendSending(false)
		}
	}

	const sendFilteredClients = useMemo(() => {
		let list = sendAllClients
		if (sendClientSearch.trim()) {
			const q = sendClientSearch.toLowerCase()
			list = list.filter(c => {
				const name = [c.name, c.last_name].filter(Boolean).join(' ').toLowerCase()
				return name.includes(q) || (c.email || '').toLowerCase().includes(q) || (c.phone_number || '').includes(q)
			})
		}
		if (sendFilterTab !== 'all') {
			list = list.filter(c => {
				const status = sendClientStatuses[c.id]
				if (sendFilterTab === 'not_sent') return !status
				if (sendFilterTab === 'pending') return status === 'pending'
				if (sendFilterTab === 'signed') return status === 'signed'
				return true
			})
		}
		return list
	}, [sendAllClients, sendClientSearch, sendFilterTab, sendClientStatuses])

	const filteredSent = useMemo(() => {
		if (!sentSearch) return sentAgreements
		const q = sentSearch.toLowerCase()
		return sentAgreements.filter(s => {
			const clientName = [s.clients?.name, s.clients?.last_name].filter(Boolean).join(' ').toLowerCase()
			const agreementTitle = s.agreements?.title?.toLowerCase() || ''
			return clientName.includes(q) || agreementTitle.includes(q)
		})
	}, [sentAgreements, sentSearch])

	const sentDateLabel = useMemo(() => {
		if (sentCustomRange) return `${sentCustomRange.from} – ${sentCustomRange.to}`
		return QUICK_DATE_OPTIONS.find(o => o.value === sentDateKey)?.label || 'This Month'
	}, [sentDateKey, sentCustomRange])

	const filteredManage = agreements.filter(a => {
		if (!manageSearch) return true
		return a.title.toLowerCase().includes(manageSearch.toLowerCase())
	})

	const sigTabCounts = useMemo(() => {
		const now = new Date()
		const twoDaysFromNow = new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000)
		const total = sigTabAllStatuses.length
		const signed = sigTabAllStatuses.filter(s => s.status === 'signed').length
		const pending = sigTabAllStatuses.filter(s => s.status === 'pending').length
		const expiringSoon = sigTabAllStatuses.filter(s => {
			if (s.status !== 'pending') return false
			const startTs = s.appointments?.start_ts ? new Date(s.appointments.start_ts) : null
			if (!startTs) return false
			return startTs > now && startTs <= twoDaysFromNow
		}).length
		return { total, signed, pending, expiringSoon }
	}, [sigTabAllStatuses])

	const getSigRowDisplayStatus = (row) => {
		if (row.status === 'signed') {
			// In-person signatures are recorded via handleMarkSignedInPerson,
			// which only flips status to 'signed' without uploading a
			// signature image. So a signed row with no signature_path means
			// the merchant marked it signed in-person.
			if (!row.signature_path) {
				return { label: 'Signed in-person', bg: '#B2F5EA', text: '#2C7A7B' }
			}
			return { label: 'Signed', bg: '#B2F5EA', text: '#2C7A7B' }
		}
		if (row.status === 'pending') {
			const now = new Date()
			const twoDaysFromNow = new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000)
			const startTs = row.appointments?.start_ts ? new Date(row.appointments.start_ts) : null
			if (startTs && startTs > now && startTs <= twoDaysFromNow) {
				return { label: 'Expiring Soon', bg: '#FFE1DD', text: '#D72020' }
			}
			return { label: 'Pending', bg: '#FFF9D4', text: '#DA951F' }
		}
		return { label: row.status, bg: '#E2E8F0', text: '#4A5568' }
	}

	const formatDate = (d) => d ? dayjs(d).format('MMM D, YYYY h:mm A') : '—'

	const tabButtonSx = (isActive) => ({
		fontFamily: 'Inter, sans-serif',
		fontWeight: 600,
		fontSize: '14px',
		lineHeight: '1.21em',
		color: isActive ? '#FFFFFF' : '#2D3748',
		bgcolor: isActive ? '#9F7AEA' : '#FFFFFF',
		border: `1px solid ${isActive ? '#9F7AEA' : '#CBD5E0'}`,
		px: '16px',
		py: '10px',
		textTransform: 'none',
		minWidth: 0,
		'&:hover': {
			bgcolor: isActive ? '#805AD5' : '#F7FAFC',
		},
	})

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '20px', bgcolor: '#ffffff', borderRadius: '12px' }}>
			{/* Header */}
			<Box sx={{ mb: 3 }}>
				<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '28px', lineHeight: '1.286em', color: '#000000' }}>
					Client Agreements
				</Typography>
			</Box>

			{/* Tab Toggle */}
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
				<Box sx={{ display: 'flex' }}>
					<Button
						onClick={() => setActiveTab('sent')}
						sx={{ ...tabButtonSx(activeTab === 'sent'), borderRadius: '6px 0 0 6px' }}
					>
						Sent Agreements
					</Button>
					<Button
						onClick={() => setActiveTab('manage')}
						sx={{ ...tabButtonSx(activeTab === 'manage'), borderRadius: '0 6px 6px 0' }}
					>
						Manage Agreements
					</Button>
				</Box>
				{activeTab === 'manage' && (
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={openNewAgreement}
						sx={{
							bgcolor: '#9F7AEA',
							textTransform: 'none',
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: '14px',
							borderRadius: '6px',
							'&:hover': { bgcolor: '#805AD5' },
						}}
					>
						New Agreement
					</Button>
				)}
			</Box>

			{/* Search + Filter Bar */}
			<Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
				<TextField
					size="small"
					placeholder={activeTab === 'sent' ? 'Search clients...' : 'Search agreements...'}
					value={activeTab === 'sent' ? sentSearch : manageSearch}
					onChange={(e) => activeTab === 'sent' ? setSentSearch(e.target.value) : setManageSearch(e.target.value)}
					sx={{ flex: 1, minWidth: 200, maxWidth: 400 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon sx={{ color: '#A0AEC0', fontSize: 20 }} />
							</InputAdornment>
						),
						sx: { borderRadius: '1000px', fontSize: '14px', fontFamily: 'Inter, sans-serif', bgcolor: '#FFFFFF', border: '1px solid rgba(0,0,0,0.2)', '& fieldset': { border: 'none' } }
					}}
				/>
				{activeTab === 'sent' && (
					<>
						<AgrFilterButton
							label="Agreement"
							value={AGREEMENT_STATUS_OPTIONS.find(o => o.value === sentStatusFilter)?.label || 'All'}
							onClick={(e) => setStatusAnchor(e.currentTarget)}
						/>
						<AgrFilterButton
							label="Date"
							value={sentDateLabel}
							onClick={(e) => setDateAnchor(e.currentTarget)}
						/>
						<AgrFilterDropdown
							anchorEl={statusAnchor}
							open={Boolean(statusAnchor)}
							onClose={() => setStatusAnchor(null)}
							options={AGREEMENT_STATUS_OPTIONS}
							selected={sentStatusFilter}
							onSelect={(v) => setSentStatusFilter(v)}
						/>
						<AgrDateFilterDropdown
							anchorEl={dateAnchor}
							open={Boolean(dateAnchor)}
							onClose={() => setDateAnchor(null)}
							isMobile={isMobile}
							selectedQuickDate={sentDateKey}
							onSelectQuickDate={(v) => { if (v) { setSentDateKey(v); setSentCustomRange(null) } }}
							customRange={sentCustomRange}
							onApplyCustomRange={(range) => { setSentCustomRange(range); setSentDateKey('custom') }}
						/>
					</>
				)}
			</Box>

			{/* Summary Cards (sent tab only) */}
			{activeTab === 'sent' && (
				<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', mb: 3 }}>
					{[
						{ label: 'Total Sent', value: totalSent, icon: 'icon-park-outline:agreement', bg: '#FEE2FF', iconColor: '#BF00FF' },
						{ label: 'Pending', value: pendingCount, icon: 'ic:outline-pending-actions', bg: '#FFF9D4', iconColor: '#DA951F' },
						{ label: 'Signed', value: signedCount, icon: 'fluent:signed-24-regular', bg: '#B2F5EA', iconColor: '#2C7A7B' },
						{ label: 'Expiring Soon', value: expiringSoonCount, icon: 'famicons:warning-outline', bg: '#FFE1DD', iconColor: '#D72020' },
					].map((card) => (
						<Box key={card.label} sx={{ bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.12)', p: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '13px', color: 'rgba(0,0,0,0.6)' }}>{card.label}</Typography>
								<Box sx={{ width: 25, height: 25, borderRadius: '50%', bgcolor: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
									<Iconify icon={card.icon} width={13} height={13} color={card.iconColor} />
								</Box>
							</Box>
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '22px', color: '#000' }}>{card.value}</Typography>
						</Box>
					))}
				</Box>
			)}

			{/* Sent Agreements Table */}
			{activeTab === 'sent' && (
				<Box sx={{ bgcolor: '#FFFFFF', borderRadius: '12px', flex: 1, overflow: 'auto' }}>
					{sentLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: '#9F7AEA' }} /></Box>
					) : filteredSent.length === 0 ? (
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#718096', textAlign: 'center', py: 6 }}>No sent agreements found</Typography>
					) : (
						<Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
							<Box component="thead">
								<Box component="tr" sx={{ borderBottom: '1px solid #E2E8F0' }}>
									{[
										{ label: 'Agreement', key: 'agreement' },
										{ label: 'Client', key: 'client' },
										{ label: 'Status', key: 'status' },
										{ label: 'Action Date', key: 'date' },
										{ label: 'Actions', key: null },
									].map(({ label, key }) => {
										const isActive = key && sentSortKey === key
										const arrow = isActive ? (sentSortDir === 'asc' ? ' ↑' : ' ↓') : ''
										return (
											<Box
												component="th"
												key={label}
												onClick={key ? () => {
													if (sentSortKey === key) setSentSortDir(d => d === 'asc' ? 'desc' : 'asc')
													else { setSentSortKey(key); setSentSortDir('asc') }
												} : undefined}
												sx={{ textAlign: key ? 'left' : 'center', p: '12px 16px', fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '13px', color: isActive ? '#9F7AEA' : '#718096', cursor: key ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap', '&:hover': key ? { color: '#9F7AEA' } : {} }}
											>
												{label}{arrow}
											</Box>
										)
									})}
								</Box>
							</Box>
							<Box component="tbody">
								{filteredSent.map((row, i) => {
									const displayStatus = getSigRowDisplayStatus(row)
									const clickable = row.status === 'signed'
									return (
										<Box
											component="tr"
											key={row.id}
											onClick={clickable ? () => handleOpenSigView(row) : undefined}
											sx={{
												borderBottom: '1px solid #E2E8F0',
												bgcolor: i % 2 === 1 ? '#F9FAFB' : '#FFFFFF',
												transition: 'background-color 0.12s ease',
												cursor: clickable ? 'pointer' : 'default',
												'&:hover': { bgcolor: '#F3E8FF' },
											}}
										>
											<Box component="td" sx={{ p: '12px 16px', fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#2D3748' }}>
												{row.agreements?.title || '—'}
											</Box>
											<Box component="td" sx={{ p: '12px 16px', fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#2D3748' }}>
												{[row.clients?.name, row.clients?.last_name].filter(Boolean).join(' ') || '—'}
											</Box>
											<Box component="td" sx={{ p: '12px 16px' }}>
												<Box sx={{
													display: 'inline-flex',
													px: '10px',
													py: '3px',
													borderRadius: '9999px',
													bgcolor: displayStatus.bg,
												}}>
													<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 500, color: displayStatus.text }}>
														{displayStatus.label}
													</Typography>
												</Box>
											</Box>
											<Box component="td" sx={{ p: '12px 16px', fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#2D3748' }}>
												{row.status === 'signed'
													? `Signed: ${formatDate(row.signed_at)}`
													: `Sent: ${formatDate(row.created_at)}`
												}
											</Box>
											<Box component="td" sx={{ p: '12px 16px', textAlign: 'center' }}>
												<Box sx={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
													{row.status === 'signed' && (
														<IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenSigView(row) }} sx={{ '&:hover': { bgcolor: '#F3E8FF' } }}>
															<Iconify icon="lucide:eye" width={20} height={20} color="#2D3748" />
														</IconButton>
													)}
													{row.status === 'pending' && (
														<IconButton size="small" onClick={(e) => { e.stopPropagation(); setSentActionAnchor(e.currentTarget); setSentActionRow(row) }} sx={{ '&:hover': { bgcolor: '#F3E8FF' } }}>
															<Iconify icon="mdi:sign" width={20} height={20} color="#2D3748" />
														</IconButton>
													)}
												</Box>
											</Box>
										</Box>
									)
								})}
							</Box>
						</Box>
					)}
					<div style={{ marginRight: '10px' }}>
						<PaginationControls page={sentPage} totalCount={sentTotalCount} pageSize={PAGE_SIZE} onPageChange={setSentPage} />
					</div>
				</Box>
			)}

			{/* Sent agreements action popover */}
			<Popover
				open={Boolean(sentActionAnchor)}
				anchorEl={sentActionAnchor}
				onClose={() => { setSentActionAnchor(null); setSentActionRow(null) }}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				slotProps={{ paper: { sx: { borderRadius: '12px', boxShadow: '0px 4px 4px -4px rgba(12,12,13,0.05), 0px 16px 16px -8px rgba(12,12,13,0.1)', border: '1px solid rgba(0,0,0,0.2)', p: '12px', mt: '4px' } } }}
			>
				<Box
					onClick={() => sentActionRow && handleMarkSignedInPerson(sentActionRow, 'sent')}
					sx={{ p: '14px 0px', borderRadius: '12px', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
				>
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '14px', color: '#000' }}>Mark signed in-person</Typography>
				</Box>
			</Popover>

			{/* Manage Agreements Table */}
			{activeTab === 'manage' && (
				<Box sx={{ bgcolor: '#FFFFFF', borderRadius: '12px', flex: 1, overflow: 'auto' }}>
					{manageLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: '#9F7AEA' }} /></Box>
					) : filteredManage.length === 0 ? (
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#718096', textAlign: 'center', py: 6 }}>No agreements created yet</Typography>
					) : (
						<Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
							<Box component="thead">
								<Box component="tr" sx={{ borderBottom: '1px solid #E2E8F0' }}>
									{['Agreement name', 'Frequency', 'Status', 'Created', 'Actions'].map((h) => (
										<Box component="th" key={h} sx={{ textAlign: 'left', p: '12px 16px', fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '13px', color: '#718096' }}>{h}</Box>
									))}
								</Box>
							</Box>
							<Box component="tbody">
								{filteredManage.map((row, i) => {
									const freqLabel = row.frequency === 'every_booking' ? 'Every booking' : row.frequency === 'once' ? 'Once' : 'Manual'
									return (
										<Box
											component="tr"
											key={row.id}
											onClick={() => openEditAgreement(row)}
											sx={{
												borderBottom: '1px solid #E2E8F0',
												bgcolor: i % 2 === 1 ? '#F9FAFB' : '#FFFFFF',
												cursor: 'pointer',
												'&:hover': { bgcolor: '#F3E8FF' },
												transition: 'background-color 0.12s ease',
											}}
										>
											<Box component="td" sx={{ p: '12px 16px', fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#2D3748', fontWeight: 500 }}>
												{row.title}
											</Box>
											<Box component="td" sx={{ p: '12px 16px', fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#718096' }}>
												{freqLabel}
											</Box>
											<Box component="td" sx={{ p: '12px 16px' }} onClick={(e) => e.stopPropagation()}>
												<Switch
													checked={row.enabled}
													onChange={() => handleToggleEnabled(row)}
													size="small"
													sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#9F7AEA' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#D6BCFA' } }}
												/>
											</Box>
											<Box component="td" sx={{ p: '12px 16px', fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#718096' }}>
												{dayjs(row.created_at).format('MMM D, YYYY')}
											</Box>
											<Box component="td" sx={{ p: '12px 16px', display: 'flex', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
												<IconButton size="small" onClick={() => openEditAgreement(row)}>
													<EditIcon sx={{ fontSize: 18, color: '#718096' }} />
												</IconButton>
												<IconButton size="small" onClick={() => handleDeleteAgreement(row.id)}>
													<DeleteIcon sx={{ fontSize: 18, color: '#E53E3E' }} />
												</IconButton>
											</Box>
										</Box>
									)
								})}
							</Box>
						</Box>
					)}
				</Box>
			)}

			{/* New / Edit Agreement Dialog */}
			<Dialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				maxWidth={false}
				PaperProps={{
					sx: {
						width: isMobile ? '100%' : 800,
						height: '90vh',
						borderRadius: '16px',
						p: 0,
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden',
					}
				}}
			>
				{/* ── CREATE layout (new Figma design) ── */}
				{!editingAgreement && (<>
					<Box sx={{ flex: 1, overflow: 'auto', p: '40px 30px', display: 'flex', flexDirection: 'column', gap: '30px' }}>
						<IconButton onClick={() => setDialogOpen(false)} sx={{ position: 'absolute', top: 21, right: 25, zIndex: 10 }}>
							<CloseIcon />
						</IconButton>
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '20px', color: '#000', pr: '40px' }}>
							Create New Agreement
						</Typography>

						{/* Title field */}
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '16px', color: '#000' }}>Agreement title *</Typography>
							<Box sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: '6px', p: '12px 16px' }}>
								<TextField value={agreementTitle} onChange={(e) => setAgreementTitle(e.target.value)} placeholder="Title of agreement" variant="standard" fullWidth
									InputProps={{ disableUnderline: true, sx: { fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', color: 'rgba(0,0,0,0.6)' } }} />
							</Box>
						</Box>

						{/* Content type picker */}
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '16px', color: '#000' }}>Upload or create your agreement *</Typography>
							<Box sx={{ display: 'flex', gap: '10px' }}>
								{[
									{ val: 'pdf', label: 'PDF Upload', sub: 'Upload a PDF file from your device', icon: <UploadFileIcon sx={{ fontSize: 20, color: contentMode === 'pdf' ? '#AB04FF' : '#718096' }} /> },
									{ val: 'rich_text', label: 'Create your agreement', sub: 'Create and format your agreement', icon: <Iconify icon="streamline-ultimate:paper-write" width={22} height={22} color={contentMode === 'rich_text' ? '#AB04FF' : '#718096'} /> },
								].map(({ val, label, sub, icon }) => (
									<Box key={val} onClick={() => setContentMode(val)} sx={{
										flex: 1, border: `1px solid ${contentMode === val ? '#B521FF' : 'rgba(0,0,0,0.2)'}`, borderRadius: '12px',
										bgcolor: contentMode === val ? '#F2E7FF' : '#F7F6F3', p: '40px 6px',
										display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
										cursor: 'pointer', transition: 'all 0.15s ease',
									}}>
										<Box sx={{ width: 40, height: 40, borderRadius: '30px', bgcolor: contentMode === val ? '#E8B9FF' : 'rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
											{icon}
										</Box>
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
											<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '18px', color: '#000' }}>{label}</Typography>
											<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', color: '#4A5568' }}>{sub}</Typography>
										</Box>
									</Box>
								))}
							</Box>
						</Box>

						{/* PDF upload zone */}
						{contentMode === 'pdf' && (
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '16px', color: '#000' }}>Upload PDF *</Typography>
								<Box onClick={() => fileInputRef.current?.click()} onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }} onDragLeave={() => setIsDragOver(false)}
									onDrop={(e) => { e.preventDefault(); setIsDragOver(false); handlePdfFileSelect(e.dataTransfer.files[0]) }}
									sx={{ border: `1.5px dashed ${isDragOver ? '#B521FF' : 'rgba(0,0,0,0.25)'}`, borderRadius: '12px', bgcolor: isDragOver ? '#F2E7FF' : '#F7F6F3', p: '40px 6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', cursor: 'pointer', transition: 'all 0.15s ease', '&:hover': { borderColor: '#B521FF', bgcolor: '#F2E7FF' } }}>
									<UploadFileIcon sx={{ fontSize: 32, color: '#9F7AEA' }} />
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '15px', color: '#4A5568' }}>Click or drag to upload PDF</Typography>
								</Box>
								<input ref={fileInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={(e) => handlePdfFileSelect(e.target.files[0])} />
								{pdfFile && (
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '10px 14px', bgcolor: '#F3E8FF', borderRadius: '8px', border: '1px solid #D6BCFA' }}>
										<PdfIcon sx={{ fontSize: 20, color: '#6B46C1', flexShrink: 0 }} />
										<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#6B46C1', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pdfFile.name}</Typography>
										{pdfPreviewUrl && <Button size="small" onClick={() => setPdfPreviewOpen(true)} sx={{ textTransform: 'none', color: '#6B46C1', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '12px', p: '2px 8px', minWidth: 0, borderRadius: '6px', bgcolor: '#EDE0FF', '&:hover': { bgcolor: '#DDD0FF' }, flexShrink: 0 }}>Preview</Button>}
										<Button size="small" onClick={() => { setPdfFile(null); setPdfPreviewUrl(null) }} sx={{ textTransform: 'none', color: '#E53E3E', fontFamily: 'Inter, sans-serif', fontSize: '12px', p: '2px 6px', minWidth: 0, flexShrink: 0 }}>Remove</Button>
									</Box>
								)}
							</Box>
						)}

						{/* Rich text editor */}
						{contentMode === 'rich_text' && (
							<RichTextEditor ref={editorRef} extensions={extensions} content={agreementContent}
								onUpdate={({ editor }) => setAgreementContent(editor.getHTML())}
								sx={{ borderRadius: '6px', bgcolor: '#FFFFFF', '& .ProseMirror': { minHeight: 200, maxHeight: 260, overflowY: 'auto', cursor: 'text', '& ul, & ol': { paddingLeft: '24px' } } }}
								renderControls={() => (<><MenuControlsContainer><MenuSelectHeading /><MenuDivider /><MenuButtonBold /><MenuButtonItalic /><MenuButtonUnderline /><MenuButtonStrikethrough /><MenuDivider /><MenuButtonBulletedList /><MenuButtonOrderedList /><MenuDivider /><MenuButtonEditLink /></MenuControlsContainer><LinkBubbleMenu /></>)}
							/>
						)}

						{/* Settings */}
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '16px', color: '#000' }}>Settings:</Typography>
							{[
								{ label: 'Auto-send for new clients', desc: 'Automatically send when a new client books for the first time.', checked: autoSend, onChange: (v) => { setAutoSend(v); if (!v) setEveryAppt(false) } },
								{ label: 'Require signature before every appointment', desc: 'Clients must sign this agreement before each appointment.', checked: everyAppt, onChange: (v) => { setEveryAppt(v); if (v) setAutoSend(true) } },
							].map(({ label, desc, checked, onChange }) => (
								<Box key={label} sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: '12px', p: '14px 24px' }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
										<Box sx={{ flex: 1 }}>
											<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', fontWeight: 500, color: '#2D3748' }}>{label}</Typography>
											<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', lineHeight: '1.6em', color: '#9F9F9F', mt: '3px' }}>{desc}</Typography>
										</Box>
										<Switch checked={checked} onChange={(e) => onChange(e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#9F7AEA' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#D6BCFA' } }} />
									</Box>
								</Box>
							))}
						</Box>
					</Box>

					{/* Fixed footer */}
					<Box sx={{ flexShrink: 0, borderTop: '1px solid #CBD5E0', p: '16px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
						<Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', px: '32px', py: '10px', borderRadius: '30px', border: '1px solid rgba(0,0,0,0.2)', color: '#2D3748', bgcolor: '#FFFFFF', '&:hover': { bgcolor: '#F7FAFC' } }}>Cancel</Button>
						<Button variant="contained" disabled={saving || !agreementTitle.trim()} onClick={handleSaveAgreement}
							sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', px: '32px', py: '10px', borderRadius: '30px', bgcolor: '#C85AFF', border: '1px solid #9F7AEA', '&:hover': { bgcolor: '#B521FF' }, '&:disabled': { bgcolor: 'rgba(200,90,255,0.4)', color: '#FFFFFF' } }}>
							{saving ? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} /> : 'Create Agreement'}
						</Button>
					</Box>
				</>)}

				{/* ── EDIT layout ── */}
				{editingAgreement && (<>
					<Box sx={{ flex: 1, overflow: 'auto', p: '40px 30px', display: 'flex', flexDirection: 'column', gap: '30px' }}>
						<IconButton onClick={() => setDialogOpen(false)} sx={{ position: 'absolute', top: 21, right: 25, zIndex: 10 }}>
							<CloseIcon />
						</IconButton>

						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '20px', color: '#000', pr: '40px' }}>{agreementTitle}</Typography>

						{/* Tabs */}
						<Box sx={{ display: 'flex', borderBottom: '0.8px solid #CBD5E0' }}>
							{['Signatures', 'Content', 'Settings'].map((tab) => {
								const val = tab.toLowerCase(); const isActive = dialogTab === val
								return (
									<Box key={val} onClick={() => setDialogTab(val)} sx={{ flex: 1, textAlign: 'center', py: '14px', cursor: 'pointer', borderBottom: isActive ? '3px solid #B521FF' : 'none' }}>
										<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '16px', color: isActive ? '#B521FF' : 'rgba(0,0,0,0.5)' }}>{tab}</Typography>
									</Box>
								)
							})}
						</Box>

						{/* Signatures tab */}
						{dialogTab === 'signatures' && (
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
								{/* Summary cards */}
								<Box sx={{ display: 'flex', gap: '20px' }}>
									{[
										{ label: 'Pending', value: `${sigTabCounts.pending} of ${sigTabCounts.total} sent`, icon: 'ic:outline-pending-actions', bg: '#FFF9D4', iconColor: '#DA951F' },
										{ label: 'Signed', value: `${sigTabCounts.signed} of ${sigTabCounts.total} sent`, icon: 'fluent:signed-24-regular', bg: '#B2F5EA', iconColor: '#2C7A7B' },
										{ label: 'Expiring Soon', value: `${sigTabCounts.expiringSoon} of ${sigTabCounts.pending} pending`, icon: 'famicons:warning-outline', bg: '#FFE1DD', iconColor: '#D72020' },
									].map((card) => (
										<Box key={card.label} sx={{ flex: 1, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.12)', p: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
											<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
												<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '16px', color: 'rgba(0,0,0,0.8)' }}>{card.label}</Typography>
												<Box sx={{ width: 25, height: 25, borderRadius: '50%', bgcolor: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
													<Iconify icon={card.icon} width={13} height={13} color={card.iconColor} />
												</Box>
											</Box>
											<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '22px', color: '#000' }}>{card.value}</Typography>
										</Box>
									))}
								</Box>

								{/* Signatures header + Send button */}
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '20px', color: '#000', flex: 1 }}>Signatures</Typography>
									<Box onClick={() => { setSendDialogOpen(true); setSendClientSearch(''); setSendSelectedIds(new Set()); setSendFilterTab('all'); setSendProgress(null) }}
										sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '16px', py: '10px', bgcolor: '#FFFFFF', border: '1px solid #CBD5E0', borderRadius: '6px', cursor: 'pointer', '&:hover': { bgcolor: '#F7FAFC' } }}>
										<Iconify icon="mynaui:send" width={17} height={17} color="#000" />
										<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '14px', color: '#000' }}>Send agreement</Typography>
									</Box>
								</Box>

								{/* Signatures table */}
								<Box sx={{ bgcolor: '#FFFFFF', borderRadius: '12px', overflow: 'auto' }}>
									{sigTabLoading ? (
										<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: '#9F7AEA' }} /></Box>
									) : sigTabData.length === 0 ? (
										<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#718096', textAlign: 'center', py: 6 }}>No signatures yet</Typography>
									) : (
										<Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
											<Box component="thead">
												<Box component="tr" sx={{ borderBottom: '1px solid #E2E8F0' }}>
													{['Client', 'Status', 'Sent date', 'Signed date', 'Actions'].map((h) => (
														<Box component="th" key={h} sx={{ textAlign: h === 'Actions' ? 'center' : 'left', p: '16px 12px', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '12px', color: '#2D3748', whiteSpace: 'nowrap' }}>{h}</Box>
													))}
												</Box>
											</Box>
											<Box component="tbody">
												{sigTabData.map((row, i) => {
													const displayStatus = getSigRowDisplayStatus(row)
													const clickable = row.status === 'signed'
													return (
														<Box component="tr" key={row.id}
															onClick={clickable ? () => handleOpenSigView(row) : undefined}
															sx={{ borderBottom: '1px solid #E2E8F0', bgcolor: i % 2 === 1 ? '#F9FAFB' : '#FFFFFF', cursor: clickable ? 'pointer' : 'default', transition: 'background-color 0.12s ease', '&:hover': clickable ? { bgcolor: '#F3E8FF' } : {} }}
														>
															<Box component="td" sx={{ p: '16px 12px', fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#2D3748' }}>
																{[row.clients?.name, row.clients?.last_name].filter(Boolean).join(' ') || '—'}
															</Box>
															<Box component="td" sx={{ p: '16px 12px' }}>
																<Box sx={{ display: 'inline-flex', px: '12px', py: '4px', borderRadius: '9999px', bgcolor: displayStatus.bg }}>
																	<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 400, color: displayStatus.text }}>{displayStatus.label}</Typography>
																</Box>
															</Box>
															<Box component="td" sx={{ p: '16px 12px', fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#2D3748' }}>
																{formatDate(row.created_at)}
															</Box>
															<Box component="td" sx={{ p: '16px 12px', fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#2D3748' }}>
																{row.signed_at ? formatDate(row.signed_at) : '—'}
															</Box>
															<Box component="td" sx={{ p: '16px 12px', textAlign: 'center' }}>
																<Box sx={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
																	{row.status === 'signed' && (
																		<IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenSigView(row) }} sx={{ '&:hover': { bgcolor: '#F3E8FF' } }}>
																			<Iconify icon="lucide:eye" width={20} height={20} color="#2D3748" />
																		</IconButton>
																	)}
																	{row.status === 'pending' && (
																		<IconButton size="small" onClick={(e) => { e.stopPropagation(); setSigTabActionAnchor(e.currentTarget); setSigTabActionRow(row) }} sx={{ '&:hover': { bgcolor: '#F3E8FF' } }}>
																			<Iconify icon="mdi:sign" width={20} height={20} color="#2D3748" />
																		</IconButton>
																	)}
																</Box>
															</Box>
														</Box>
													)
												})}
											</Box>
										</Box>
									)}
									<PaginationControls page={sigTabPage} totalCount={sigTabTotalCount} pageSize={PAGE_SIZE} onPageChange={setSigTabPage} />
								</Box>

								{/* Action popover for pending rows */}
								<Popover
									open={Boolean(sigTabActionAnchor)}
									anchorEl={sigTabActionAnchor}
									onClose={() => { setSigTabActionAnchor(null); setSigTabActionRow(null) }}
									anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
									transformOrigin={{ vertical: 'top', horizontal: 'left' }}
									slotProps={{ paper: { sx: { borderRadius: '12px', boxShadow: '0px 4px 4px -4px rgba(12,12,13,0.05), 0px 16px 16px -8px rgba(12,12,13,0.1)', border: '1px solid rgba(0,0,0,0.2)', p: '12px', mt: '4px' } } }}
								>
									<Box
										onClick={() => sigTabActionRow && handleMarkSignedInPerson(sigTabActionRow, 'sigTab')}
										sx={{ p: '14px 0px', borderRadius: '12px', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
									>
										<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '14px', color: '#000' }}>Mark signed in-person</Typography>
									</Box>
								</Popover>
							</Box>
						)}

						{/* Sub-header row below tabs: agreement name + Edit button (content tab only, hidden in edit mode) */}
						{!isEditMode && dialogTab === 'content' && (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '20px', color: '#000', flex: 1 }}>{agreementTitle}</Typography>
								<Box onClick={() => { editSnapshotRef.current = { title: agreementTitle, content: agreementContent, contentMode, pdfFile, existingPdfPath }; setIsEditMode(true) }}
									sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '16px', py: '10px', border: '1px solid rgba(0,0,0,0.2)', borderRadius: '10px', cursor: 'pointer', userSelect: 'none', flexShrink: 0, '&:hover': { bgcolor: '#F7F6F3' } }}>
									<EditIcon sx={{ width: 20, height: 20, color: '#000' }} />
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '18px', color: '#000', lineHeight: 1 }}>Edit</Typography>
								</Box>
							</Box>
						)}

						{/* Content tab */}
						{dialogTab === 'content' && (
							<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
								{isEditMode && (
									<Box sx={{ display: 'flex', gap: '8px' }}>
										{[{ label: 'Rich Text', val: 'rich_text' }, { label: 'PDF Upload', val: 'pdf' }].map(({ label, val }) => (
											<Button key={val} onClick={() => setContentMode(val)} size="small" sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '13px', px: '14px', py: '6px', borderRadius: '6px', border: `1px solid ${contentMode === val ? '#9F7AEA' : '#CBD5E0'}`, bgcolor: contentMode === val ? '#F3E8FF' : 'transparent', color: contentMode === val ? '#6B46C1' : '#718096', '&:hover': { bgcolor: contentMode === val ? '#EDE0FF' : '#F7FAFC' } }}>{label}</Button>
										))}
									</Box>
								)}

								{/* Agreement title field — shown in edit mode above the editor */}
								{isEditMode && (
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
										<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', color: '#000' }}>Agreement title *</Typography>
										<Box sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: '6px', p: '12px 16px' }}>
											<TextField value={agreementTitle} onChange={(e) => setAgreementTitle(e.target.value)} placeholder="Title of agreement" variant="standard" fullWidth
												InputProps={{ disableUnderline: true, sx: { fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', color: 'rgba(0,0,0,0.6)' } }} />
										</Box>
									</Box>
								)}

								{contentMode === 'rich_text' ? (
									isEditMode ? (
										<Box sx={{ minHeight: 300 }}>
											<RichTextEditor ref={editorRef} extensions={extensions} content={agreementContent} onUpdate={({ editor }) => setAgreementContent(editor.getHTML())} sx={{ borderRadius: '6px', bgcolor: '#FFFFFF', minHeight: 280, '& .ProseMirror': { minHeight: 220, maxHeight: 300, overflowY: 'auto', cursor: 'text', '& ul, & ol': { paddingLeft: '24px' } } }}
												renderControls={() => (<><MenuControlsContainer><MenuSelectHeading /><MenuDivider /><MenuButtonBold /><MenuButtonItalic /><MenuButtonUnderline /><MenuButtonStrikethrough /><MenuDivider /><MenuButtonBulletedList /><MenuButtonOrderedList /><MenuDivider /><MenuButtonEditLink /></MenuControlsContainer><LinkBubbleMenu /></>)} />
										</Box>
									) : (
										<Box sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#2D3748', lineHeight: '1.7', p: '4px 2px', '& p': { margin: '0 0 8px 0' }, '& ul, & ol': { paddingLeft: '24px', margin: '0 0 8px 0' }, '& h1,& h2,& h3': { margin: '0 0 8px 0' } }}
											dangerouslySetInnerHTML={{ __html: agreementContent || '<p style="color:#A0AEC0">No content</p>' }} />
									)
								) : (
									isEditMode ? (
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
											<Box onClick={() => fileInputRef.current?.click()} onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }} onDragLeave={() => setIsDragOver(false)}
												onDrop={(e) => { e.preventDefault(); setIsDragOver(false); handlePdfFileSelect(e.dataTransfer.files[0]) }}
												sx={{ border: `2px dashed ${isDragOver ? '#9F7AEA' : '#CBD5E0'}`, borderRadius: '10px', bgcolor: isDragOver ? '#F3E8FF' : '#FAFAFA', p: '28px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: 'pointer', '&:hover': { borderColor: '#9F7AEA', bgcolor: '#F9F0FF' } }}>
												<UploadFileIcon sx={{ fontSize: 36, color: '#9F7AEA' }} />
												<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', color: '#2D3748' }}>{pdfFile ? pdfFile.name : 'Click or drag to upload PDF'}</Typography>
												{!pdfFile && <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#A0AEC0' }}>PDF files only</Typography>}
											</Box>
											<input ref={fileInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={(e) => handlePdfFileSelect(e.target.files[0])} />
											{(pdfFile || existingPdfPath) && (
												<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '10px 14px', bgcolor: '#F3E8FF', borderRadius: '8px', border: '1px solid #D6BCFA' }}>
													<PdfIcon sx={{ fontSize: 20, color: '#6B46C1', flexShrink: 0 }} />
													<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#6B46C1', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pdfFile ? pdfFile.name : existingPdfPath.split('/').pop()}</Typography>
													{pdfPreviewUrl && <Button size="small" onClick={() => setPdfPreviewOpen(true)} sx={{ textTransform: 'none', color: '#6B46C1', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '12px', p: '2px 8px', minWidth: 0, borderRadius: '6px', bgcolor: '#EDE0FF', '&:hover': { bgcolor: '#DDD0FF' }, flexShrink: 0 }}>Preview</Button>}
													{pdfFile && <Button size="small" onClick={() => { setPdfFile(null); setPdfPreviewUrl(null) }} sx={{ textTransform: 'none', color: '#E53E3E', fontFamily: 'Inter, sans-serif', fontSize: '12px', p: '2px 6px', minWidth: 0, flexShrink: 0 }}>Remove</Button>}
												</Box>
											)}
										</Box>
									) : (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '10px 14px', bgcolor: '#F3E8FF', borderRadius: '8px', border: '1px solid #D6BCFA' }}>
											<PdfIcon sx={{ fontSize: 20, color: '#6B46C1', flexShrink: 0 }} />
											<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#6B46C1', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{existingPdfPath?.split('/').pop() || 'PDF'}</Typography>
											{pdfPreviewUrl && <Button size="small" onClick={() => setPdfPreviewOpen(true)} sx={{ textTransform: 'none', color: '#6B46C1', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '12px', p: '2px 8px', minWidth: 0, borderRadius: '6px', bgcolor: '#EDE0FF', '&:hover': { bgcolor: '#DDD0FF' }, flexShrink: 0 }}>Preview</Button>}
										</Box>
									)
								)}
							</Box>
						)}

						{/* Settings tab — always editable */}
						{dialogTab === 'settings' && (
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
								{[
									{ label: 'Auto-send for new clients', desc: 'Automatically send the agreement when a new client books for the first time, including online booking.', checked: autoSend, onChange: (v) => { setAutoSend(v); if (!v) setEveryAppt(false) } },
									{ label: 'Require signature before every appointment', desc: 'Clients must sign this agreement before each appointment. Unsigned appointments will be flagged.', checked: everyAppt, onChange: (v) => { setEveryAppt(v); if (v) setAutoSend(true) } },
								].map(({ label, desc, checked, onChange }) => (
									<Box key={label} sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: '12px', p: '14px 24px' }}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
											<Box sx={{ flex: 1 }}>
												<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '18px', color: '#2D3748' }}>{label}</Typography>
												<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', lineHeight: '1.75em', color: '#9F9F9F', mt: '3px' }}>{desc}</Typography>
											</Box>
											<Switch checked={checked} onChange={(e) => onChange(e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#9F7AEA' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#D6BCFA' } }} />
										</Box>
									</Box>
								))}
							</Box>
						)}
					</Box>

					{/* Fixed footer — Cancel is shown whenever there is an active
					    state to bail out of (content edit mode, or modified
					    settings toggles). The "Update Agreement" button only
					    appears when there are real changes to commit, so a
					    no-op save flow is impossible. */}
					{((dialogTab === 'content' && isEditMode) || (dialogTab === 'settings' && settingsChanged)) && (
						<Box sx={{ flexShrink: 0, borderTop: '1px solid #CBD5E0', p: '16px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
							<Button onClick={handleCancelEdit} sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', px: '32px', py: '10px', borderRadius: '30px', border: '1px solid rgba(0,0,0,0.2)', color: '#2D3748', bgcolor: '#FFFFFF', '&:hover': { bgcolor: '#F7FAFC' } }}>Cancel</Button>
							{((dialogTab === 'content' && contentChanged) || (dialogTab === 'settings' && settingsChanged)) && (
								<Button variant="contained" disabled={saving || !agreementTitle.trim()} onClick={handleSaveAgreement}
									sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', px: '32px', py: '10px', borderRadius: '30px', bgcolor: '#C85AFF', border: '1px solid #9F7AEA', '&:hover': { bgcolor: '#B521FF' }, '&:disabled': { bgcolor: 'rgba(200,90,255,0.4)', color: '#FFFFFF' } }}>
									{saving ? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} /> : 'Update Agreement'}
								</Button>
							)}
						</Box>
					)}
				</>)}
			</Dialog>

			{/* PDF Preview Dialog */}
			<Dialog
				open={pdfPreviewOpen}
				onClose={() => setPdfPreviewOpen(false)}
				maxWidth={false}
				PaperProps={{
					sx: { width: 760, maxHeight: '92vh', borderRadius: '16px', p: 0, position: 'relative', overflow: 'hidden' }
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '24px', py: '16px', borderBottom: '1px solid #E2E8F0' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<PdfIcon sx={{ fontSize: 18, color: '#6B46C1' }} />
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '15px', color: '#2D3748' }}>
							{pdfFile ? pdfFile.name : existingPdfPath?.split('/').pop() || 'Agreement PDF'}
						</Typography>
						{pdfNumPages && (
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#A0AEC0' }}>
								{pdfNumPages} {pdfNumPages === 1 ? 'page' : 'pages'}
							</Typography>
						)}
					</Box>
					<IconButton size="small" onClick={() => setPdfPreviewOpen(false)}>
						<CloseIcon sx={{ fontSize: 20 }} />
					</IconButton>
				</Box>
				<Box sx={{ overflow: 'auto', maxHeight: 'calc(92vh - 64px)', bgcolor: '#F7FAFC', display: 'flex', flexDirection: 'column', alignItems: 'center', p: '16px', gap: '8px' }}>
					<Document
						file={pdfPreviewUrl}
						onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
						loading={<CircularProgress size={28} sx={{ color: '#9F7AEA', m: 4 }} />}
					>
						{Array.from({ length: pdfNumPages || 0 }, (_, i) => (
							<Box key={i + 1} sx={{ mb: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' }}>
								<Page
									pageNumber={i + 1}
									width={700}
									renderTextLayer={false}
									renderAnnotationLayer={false}
								/>
							</Box>
						))}
					</Document>
				</Box>
			</Dialog>
			{/* Re-sign confirmation dialog — merchant must pick one of the two
			    options; there is no Cancel/X/Escape dismissal because the new
			    agreement has already been saved when this dialog opens. */}
			<Dialog
				open={resignDialogOpen}
				onClose={() => { }}
				disableEscapeKeyDown
				maxWidth={false}
				PaperProps={{
					sx: {
						borderRadius: '12px',
						border: '1px solid #CBD5E0',
						p: 0,
						maxWidth: 520,
						width: '100%',
						boxShadow: '0px 4px 4px -4px rgba(12,12,13,0.05), 0px 16px 32px -4px rgba(12,12,13,0.1)',
					}
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', p: '30px' }}>
					<Typography sx={{
						fontFamily: 'Inter, sans-serif',
						fontWeight: 600,
						fontSize: '22px',
						lineHeight: '1.09em',
						color: '#000000',
					}}>Agreement Updated</Typography>

					<Typography sx={{
						fontFamily: 'Inter, sans-serif',
						fontWeight: 500,
						fontSize: '16px',
						lineHeight: '1.5em',
						color: 'rgba(0,0,0,0.6)',
					}}>
						{resignAffectedCount > 0
							? `This agreement has been signed by ${resignAffectedCount} client${resignAffectedCount !== 1 ? 's' : ''}. Would you like to require them to re-sign the updated version?`
							: 'Would you like to require existing clients to sign this updated agreement on their next booking?'
						}
					</Typography>

					<Box sx={{ display: 'flex', gap: '12px', flexWrap: 'nowrap' }}>
						<Button
							onClick={() => handleResignChoice(true)}
							sx={{
								padding: '12px 20px',
								borderRadius: '32px',
								border: '1px solid #CBD5E0',
								bgcolor: '#FFFFFF',
								textTransform: 'none',
								fontFamily: 'Inter, sans-serif',
								fontWeight: 600,
								fontSize: '14px',
								lineHeight: '1.21em',
								letterSpacing: '-1.5625%',
								color: 'rgba(0,0,0,0.6)',
								whiteSpace: 'nowrap',
								'&:hover': { bgcolor: '#F7FAFC' },
							}}
						>
							Require re-signatures
						</Button>
						<Button
							onClick={() => handleResignChoice(false)}
							sx={{
								padding: '12px 20px',
								borderRadius: '32px',
								bgcolor: '#C85AFF',
								textTransform: 'none',
								fontFamily: 'Inter, sans-serif',
								fontWeight: 600,
								fontSize: '14px',
								lineHeight: '1.21em',
								letterSpacing: '-1.5625%',
								color: '#FFFFFF',
								whiteSpace: 'nowrap',
								'&:hover': { bgcolor: '#b84deb' },
							}}
						>
							Keep existing signatures
						</Button>
					</Box>
				</Box>
			</Dialog>

			{/* Send Agreement to Clients Dialog */}
			<Dialog
				open={sendDialogOpen}
				onClose={() => !sendSending && setSendDialogOpen(false)}
				maxWidth={false}
				PaperProps={{ sx: { width: isMobile ? '100%' : 850, height: '90vh', borderRadius: '16px', p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
			>
				<Box sx={{ flex: 1, overflow: 'auto', p: '40px 30px', display: 'flex', flexDirection: 'column', gap: '30px' }}>
					<IconButton onClick={() => !sendSending && setSendDialogOpen(false)} sx={{ position: 'absolute', top: 21, right: 25, zIndex: 10 }}>
						<CloseIcon />
					</IconButton>

					{/* Title + progress */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', pr: '40px' }}>
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '20px', color: '#000' }}>
							Send Agreement to Clients
						</Typography>
						{sendProgress && (
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '14px', color: '#9F7AEA' }}>
								{sendProgress.sent}/{sendProgress.total} sent
							</Typography>
						)}
					</Box>

					{/* Filter tabs */}
					<Box sx={{ display: 'flex', borderBottom: '0.8px solid #CBD5E0' }}>
						{[
							{ val: 'not_sent', label: 'Not Sent' },
							{ val: 'pending', label: 'Pending' },
							{ val: 'signed', label: 'Signed' },
							{ val: 'all', label: 'All' },
						].map(({ val, label }) => {
							const isActive = sendFilterTab === val
							return (
								<Box key={val} onClick={() => setSendFilterTab(val)} sx={{ flex: 1, textAlign: 'center', py: '14px', cursor: 'pointer', borderBottom: isActive ? '3px solid #B521FF' : 'none' }}>
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '16px', color: isActive ? '#B521FF' : 'rgba(0,0,0,0.5)' }}>{label}</Typography>
								</Box>
							)
						})}
					</Box>

					{/* Clients section */}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', flex: 1, minHeight: 0 }}>
						{/* Search + actions bar */}
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
								<TextField
									size="small"
									placeholder="Search clients"
									value={sendClientSearch}
									onChange={(e) => setSendClientSearch(e.target.value)}
									sx={{ width: 300 }}
									InputProps={{
										startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#A0AEC0', fontSize: 16 }} /></InputAdornment>,
										sx: { borderRadius: '1000px', fontSize: '14px', fontFamily: 'Inter, sans-serif', bgcolor: '#FFFFFF', border: '1px solid rgba(0,0,0,0.2)', '& fieldset': { border: 'none' } }
									}}
								/>
								<Box onClick={() => { setSendSelectedIds(new Set()); setSendClientSearch('') }}
									sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '16px', py: '10px', bgcolor: '#FFFFFF', border: '1px solid #CBD5E0', borderRadius: '6px', cursor: 'pointer', '&:hover': { bgcolor: '#F7FAFC' } }}>
									<Iconify icon="ant-design:delete-outlined" width={20} height={20} />
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', color: 'rgba(0,0,0,0.8)' }}>Clear</Typography>
								</Box>
								<Box onClick={() => setSendSelectedIds(new Set(sendFilteredClients.map(c => c.id)))}
									sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '16px', py: '10px', bgcolor: '#FFFFFF', border: '1px solid #CBD5E0', borderRadius: '6px', cursor: 'pointer', '&:hover': { bgcolor: '#F7FAFC' } }}>
									<Iconify icon="fluent:select-all-on-24-regular" width={20} height={20} />
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', color: 'rgba(0,0,0,0.8)' }}>Select All</Typography>
								</Box>
							</Box>
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '16px', color: 'rgba(0,0,0,0.6)' }}>
								{sendSelectedIds.size}/{sendFilteredClients.length} clients selected
							</Typography>
						</Box>

						{/* Client table */}
						<Box sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: '12px', flex: 1, overflow: 'auto' }}>
							{sendClientsLoading ? (
								<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: '#9F7AEA' }} /></Box>
							) : sendFilteredClients.length === 0 ? (
								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#718096', textAlign: 'center', py: 6 }}>No clients found</Typography>
							) : (
								<Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
									<Box component="thead">
										<Box component="tr" sx={{ borderBottom: '1px solid rgba(0,0,0,0.2)' }}>
											<Box component="th" sx={{ width: 46, p: '16px 12px' }} />
											{['Client', 'Phone', 'Email', 'Status'].map((h) => (
												<Box component="th" key={h} sx={{ textAlign: 'left', p: '16px 12px', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '12px', color: '#000' }}>{h}</Box>
											))}
										</Box>
									</Box>
									<Box component="tbody">
										{sendFilteredClients.map((client) => {
											const isChecked = sendSelectedIds.has(client.id)
											const clientStatus = sendClientStatuses[client.id]
											const statusDisplay = clientStatus === 'signed'
												? { label: 'Signed', bg: '#B2F5EA', text: '#2C7A7B' }
												: clientStatus === 'pending'
													? { label: 'Pending', bg: '#FFF9D4', text: '#DA951F' }
													: { label: 'Not sent', bg: 'rgba(0,0,0,0.1)', text: 'rgba(0,0,0,0.8)' }
											return (
												<Box component="tr" key={client.id}
													onClick={() => setSendSelectedIds(prev => { const next = new Set(prev); if (next.has(client.id)) next.delete(client.id); else next.add(client.id); return next })}
													sx={{ borderBottom: '1px solid rgba(0,0,0,0.2)', cursor: 'pointer', '&:hover': { bgcolor: '#F9FAFB' } }}>
													<Box component="td" sx={{ width: 46, p: '16px 12px', textAlign: 'center' }}>
														<Checkbox checked={isChecked} size="small" sx={{ p: 0, color: isChecked ? '#0AB91F' : '#CBD5E0', '&.Mui-checked': { color: '#0AB91F' } }} />
													</Box>
													<Box component="td" sx={{ p: '16px 12px', fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '14px', color: '#000' }}>
														{[client.name, client.last_name].filter(Boolean).join(' ')}
													</Box>
													<Box component="td" sx={{ p: '16px 12px', fontFamily: 'Inter, sans-serif', fontSize: '14px', color: 'rgba(0,0,0,0.8)' }}>
														{client.phone_number || '—'}
													</Box>
													<Box component="td" sx={{ p: '16px 12px', fontFamily: 'Inter, sans-serif', fontSize: '14px', color: 'rgba(0,0,0,0.8)' }}>
														{client.email || '—'}
													</Box>
													<Box component="td" sx={{ p: '16px 12px' }}>
														<Box sx={{ display: 'inline-flex', px: '12px', py: '4px', borderRadius: '9999px', bgcolor: statusDisplay.bg }}>
															<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 400, color: statusDisplay.text }}>{statusDisplay.label}</Typography>
														</Box>
													</Box>
												</Box>
											)
										})}
									</Box>
								</Box>
							)}
						</Box>
					</Box>
				</Box>

				{/* Fixed footer */}
				<Box sx={{ flexShrink: 0, borderTop: '1px solid #CBD5E0', p: '16px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
					<Button onClick={() => !sendSending && setSendDialogOpen(false)}
						sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '16px', px: '100px', py: '16px', borderRadius: '30px', border: '1px solid #CBD5E0', color: '#9F7AEA', bgcolor: '#FFFFFF', '&:hover': { bgcolor: '#F7FAFC' } }}>
						Back
					</Button>
					<Button variant="contained" disabled={sendSelectedIds.size === 0 || sendSending} onClick={handleSendToClients}
						sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '16px', px: '100px', py: '16px', borderRadius: '30px', bgcolor: '#C85AFF', border: '1px solid #9F7AEA', '&:hover': { bgcolor: '#B521FF' }, '&:disabled': { bgcolor: 'rgba(200,90,255,0.4)', color: '#FFFFFF' } }}>
						{sendSending
							? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
							: `Send to ${sendSelectedIds.size} Client${sendSelectedIds.size !== 1 ? 's' : ''}`
						}
					</Button>
				</Box>
			</Dialog>
		</Box>
	)
}
